import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { CheckCircle2, ArrowRight, Phone, Zap } from 'lucide-react';

export const DirectMax = () => {
  const navigate = useNavigate();

  const scrollToContact = () => {
    document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#0B1020]">
      <Helmet>
        <title>DirectMax - Premium Men's Wellness | Bryan Maldonado</title>
        <meta name="description" content="DirectMax is a comprehensive men's wellness solution combining targeted nutritional support with professional guidance. Enhance energy, recovery, performance, and overall vitality." />
        <meta name="keywords" content="DirectMax, men's wellness, male vitality, energy support, performance optimization, Bryan Maldonado" />
        <link rel="canonical" href={`${window.location.origin}/directmax`} />
      </Helmet>
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0B1020]/95 backdrop-blur-sm border-b border-[#1E293B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="text-xl font-bold text-[#F8FAFC] cursor-pointer" onClick={() => navigate('/')}>
              Bryan Maldonado
            </div>
            <div className="flex items-center gap-4">
              <a href="tel:801-651-8363" className="hidden sm:flex items-center gap-2 text-[#94A3B8] hover:text-[#F8FAFC] transition-colors">
                <Phone size={18} />
                <span>801-651-8363</span>
              </a>
              <Button onClick={scrollToContact} className="bg-[#22C55E] hover:bg-[#16A34A] text-white">
                Get DirectMax
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block px-4 py-2 bg-[#22C55E]/10 border border-[#22C55E]/30 rounded-full mb-6">
            <div className="flex items-center gap-2">
              <Zap className="text-[#22C55E]" size={18} />
              <p className="text-[#22C55E] text-sm font-semibold">Available Now</p>
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#F8FAFC] mb-6 leading-tight">
            DirectMax
          </h1>
          <p className="text-xl sm:text-2xl text-[#94A3B8] mb-8 max-w-3xl mx-auto">
            Premium men's wellness support designed to optimize vitality, performance, and overall well-being.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={scrollToContact}
              className="bg-[#22C55E] hover:bg-[#16A34A] text-white text-lg px-8 py-6 h-auto"
            >
              Get Started with DirectMax
              <ArrowRight className="ml-2" size={20} />
            </Button>
            <Button 
              onClick={() => window.location.href = 'tel:801-651-8363'}
              variant="outline"
              className="border-[#1E293B] bg-[#121A2B] hover:bg-[#1E293B] text-[#F8FAFC] text-lg px-8 py-6 h-auto"
            >
              <Phone className="mr-2" size={20} />
              Call Now
            </Button>
          </div>
        </div>
      </section>

      {/* What It Is */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#121A2B]/50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] text-center mb-6">
            What is DirectMax?
          </h2>
          <p className="text-[#94A3B8] text-lg text-center mb-8 max-w-3xl mx-auto">
            DirectMax is a comprehensive men's wellness solution combining targeted nutritional support with professional guidance to help you perform at your best.
          </p>
          <p className="text-[#94A3B8] text-center max-w-2xl mx-auto">
            Whether you are focused on energy, recovery, performance, or overall vitality, DirectMax provides the foundation you need with ongoing support from licensed professionals.
          </p>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] text-center mb-4">
            DirectMax Benefits
          </h2>
          <p className="text-[#94A3B8] text-center mb-12 max-w-2xl mx-auto">
            Designed for men who want to feel and perform at their peak.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Enhanced Energy', desc: 'Sustained vitality throughout your day without crashes or fatigue' },
              { title: 'Improved Recovery', desc: 'Bounce back faster from workouts and physical demands' },
              { title: 'Mental Clarity', desc: 'Sharper focus and improved cognitive performance' },
              { title: 'Physical Performance', desc: 'Support for strength, endurance, and overall athletic ability' },
              { title: 'Metabolic Support', desc: 'Help your body maintain healthy metabolism and body composition' },
              { title: 'Overall Wellness', desc: 'Comprehensive support for long-term health and vitality' }
            ].map((benefit, idx) => (
              <Card key={idx} className="bg-[#121A2B] border-[#1E293B] p-6 hover:border-[#22C55E] transition-all duration-300">
                <CheckCircle2 className="text-[#22C55E] mb-4" size={32} />
                <h3 className="text-xl font-bold text-[#F8FAFC] mb-3">{benefit.title}</h3>
                <p className="text-[#94A3B8]">{benefit.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#121A2B]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] text-center mb-16">
            How DirectMax Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                num: '1', 
                title: 'Initial Consultation', 
                desc: 'Discuss your goals, health history, and what you want to achieve with DirectMax.' 
              },
              { 
                num: '2', 
                title: 'Personalized Protocol', 
                desc: 'Receive your customized DirectMax plan tailored to your specific needs and objectives.' 
              },
              { 
                num: '3', 
                title: 'Ongoing Support', 
                desc: 'Stay connected with check-ins, adjustments, and professional guidance throughout your journey.' 
              }
            ].map((step) => (
              <Card key={step.num} className="bg-[#0B1020] border-[#1E293B] p-6 text-center hover:border-[#22C55E] transition-all duration-300">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#22C55E] to-[#16A34A] text-white text-2xl font-bold flex items-center justify-center mx-auto mb-4 shadow-lg">
                  {step.num}
                </div>
                <h3 className="text-xl font-bold text-[#F8FAFC] mb-3">{step.title}</h3>
                <p className="text-[#94A3B8]">{step.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact-section" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-5xl font-bold text-[#F8FAFC] mb-6">
            Ready to Experience DirectMax?
          </h2>
          <p className="text-xl text-[#94A3B8] mb-8 max-w-2xl mx-auto">
            Take the first step toward optimized wellness and performance. Contact Bryan directly to get started.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button 
              onClick={() => window.location.href = 'tel:801-651-8363'}
              className="bg-[#22C55E] hover:bg-[#16A34A] text-white text-lg px-8 py-6 h-auto"
            >
              <Phone className="mr-2" size={20} />
              Call: 801-651-8363
            </Button>
            <Button 
              onClick={() => window.location.href = 'sms:801-651-8363'}
              variant="outline"
              className="border-[#22C55E] bg-[#22C55E]/10 hover:bg-[#22C55E]/20 text-[#22C55E] text-lg px-8 py-6 h-auto"
            >
              Text Us
            </Button>
          </div>
          <p className="text-[#94A3B8]">
            Or email: <a href="mailto:Bryan@DirectMeds.com" className="text-[#22C55E] hover:text-[#16A34A] transition-colors">Bryan@DirectMeds.com</a>
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-[#1E293B]">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-[#94A3B8] mb-4">© 2024 Bryan Maldonado. All rights reserved.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a href="tel:801-651-8363" className="text-[#94A3B8] hover:text-[#F8FAFC] transition-colors">
              801-651-8363
            </a>
            <span className="text-[#94A3B8] hidden sm:block">|</span>
            <a href="mailto:Bryan@DirectMeds.com" className="text-[#94A3B8] hover:text-[#F8FAFC] transition-colors">
              Bryan@DirectMeds.com
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DirectMax;
